/**
 * ProductFormModal — create or edit a product.
 * Create: product == null → calls createProduct(supabase, tenantId, input).
 * Edit:   product != null → calls updateProduct(supabase, product.id, input).
 * Price and cost are stored as integer minor units (ден×100) in the DB;
 * the form fields show/accept major units (ден) and convert at the edge.
 */
import { createProduct, updateProduct } from '@gym/api-client';
import type { TaxRateCode } from '@gym/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/Button.js';
import { Field, Input, Select } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { toast } from '../../components/ui/Toaster.js';
import { usePosContext } from '../../lib/pos.js';
import { supabase } from '../../lib/supabase.js';

interface ProductRow {
  id: string;
  name: string;
  price: number;
  cost: number | null;
  category: string | null;
  sku: string | null;
  barcode: string | null;
  tax_rate_code: TaxRateCode;
  is_active: boolean;
  track_inventory: boolean;
}

export interface ProductFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null / undefined → create mode; a product row → edit mode */
  product?: ProductRow | null;
  tenantId: string;
}

const TAX_CODES: TaxRateCode[] = ['A', 'B', 'C', 'EXEMPT'];

function toMajor(minor: number | null | undefined): string {
  if (minor == null) return '';
  return (minor / 100).toFixed(2);
}

function toMinor(majorStr: string): number {
  const v = parseFloat(majorStr);
  if (Number.isNaN(v)) return 0;
  return Math.round(v * 100);
}

export function ProductFormModal({
  open,
  onOpenChange,
  product,
  tenantId,
}: ProductFormModalProps): ReactNode {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { locationId } = usePosContext();
  const isEdit = product != null;

  // ── Form state ───────────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [cost, setCost] = useState('');
  const [category, setCategory] = useState('');
  const [sku, setSku] = useState('');
  const [barcode, setBarcode] = useState('');
  const [taxCode, setTaxCode] = useState<TaxRateCode>('A');
  const [isActive, setIsActive] = useState(true);
  const [trackInventory, setTrackInventory] = useState(true);

  // ── Validation errors ────────────────────────────────────────────────────────
  const [nameError, setNameError] = useState('');
  const [priceError, setPriceError] = useState('');

  // Prefill when switching product or opening in edit mode
  useEffect(() => {
    if (open) {
      if (product) {
        setName(product.name);
        setPrice(toMajor(product.price));
        setCost(toMajor(product.cost));
        setCategory(product.category ?? '');
        setSku(product.sku ?? '');
        setBarcode(product.barcode ?? '');
        setTaxCode(product.tax_rate_code ?? 'A');
        setIsActive(product.is_active ?? true);
        setTrackInventory(product.track_inventory ?? true);
      } else {
        setName('');
        setPrice('');
        setCost('');
        setCategory('');
        setSku('');
        setBarcode('');
        setTaxCode('A');
        setIsActive(true);
        setTrackInventory(true);
      }
      setNameError('');
      setPriceError('');
    }
  }, [open, product]);

  // ── Mutation ─────────────────────────────────────────────────────────────────
  const mutation = useMutation({
    mutationFn: async () => {
      const input = {
        name: name.trim(),
        price: toMinor(price),
        cost: cost.trim() ? toMinor(cost) : null,
        category: category.trim() || null,
        sku: sku.trim() || null,
        barcode: barcode.trim() || null,
        tax_rate_code: taxCode,
        is_active: isActive,
        track_inventory: trackInventory,
      };
      if (isEdit && product) {
        return updateProduct(supabase, product.id, input);
      }
      // Create mode: seed a baseline inventory row at the active location so the
      // new product starts at stock 0 (never null "—" in Залиха).
      return createProduct(supabase, tenantId, input, locationId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] });
      void qc.invalidateQueries({ queryKey: ['inventory_qty'] });
      toast.success(isEdit ? t('retail.product.edit') : t('retail.product.add'));
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(t('common.error'), e.message),
  });

  // ── Validation + submit ──────────────────────────────────────────────────────
  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    let valid = true;

    if (!name.trim()) {
      setNameError(t('retail.product.name'));
      valid = false;
    } else {
      setNameError('');
    }

    const priceVal = parseFloat(price);
    if (!price.trim() || Number.isNaN(priceVal) || priceVal <= 0) {
      setPriceError(t('retail.product.price'));
      valid = false;
    } else {
      setPriceError('');
    }

    if (valid) mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!mutation.isPending) onOpenChange(o);
      }}
      title={isEdit ? t('retail.product.edit') : t('retail.product.add')}
      size="md"
      footer={
        <>
          <Button
            variant="secondary"
            disabled={mutation.isPending}
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            onClick={(e) => handleSubmit(e as unknown as FormEvent)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="grid gap-4">
        {/* Name */}
        <Field label={t('retail.product.name')} required error={nameError || undefined}>
          {(id) => (
            <Input
              id={id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('retail.product.name')}
            />
          )}
        </Field>

        {/* Price + Cost (major units) */}
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('retail.product.price')} required error={priceError || undefined}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min="0.01"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0.00"
              />
            )}
          </Field>

          <Field label={t('retail.product.cost')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min="0"
                step="0.01"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder="0.00"
              />
            )}
          </Field>
        </div>

        {/* Category */}
        <Field label={t('retail.product.category')}>
          {(id) => (
            <Input
              id={id}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder={t('retail.product.category')}
            />
          )}
        </Field>

        {/* SKU + Barcode */}
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('retail.product.sku')}>
            {(id) => (
              <Input
                id={id}
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                placeholder={t('retail.product.sku')}
              />
            )}
          </Field>

          <Field label={t('retail.product.barcode')}>
            {(id) => (
              <Input
                id={id}
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                placeholder={t('retail.product.barcode')}
              />
            )}
          </Field>
        </div>

        {/* Tax rate code */}
        <Field label={t('retail.product.tax_rate_code')}>
          {(id) => (
            <Select
              id={id}
              value={taxCode}
              onChange={(e) => setTaxCode(e.target.value as TaxRateCode)}
            >
              {TAX_CODES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {/* Checkboxes */}
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-primary"
            />
            {t('retail.product.active')}
          </label>

          <label className="flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={trackInventory}
              onChange={(e) => setTrackInventory(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-primary"
            />
            {t('retail.product.trackInventory')}
          </label>
        </div>
      </form>
    </Modal>
  );
}
